import type { Schemas } from '@npc/api-client'

import { api } from './api'
import { call } from './errors'

/**
 * AI OS 自进化那一组的客户端调用。**八读三写**，三个写口都不执行任何东西。
 *
 * 类型与路径全部来自 `openapi/openapi.yaml` 的生成物，一处手写的接口形状都没有。
 * OS-0 的冻结合同摘要 §5 把这一组的边界写死了，下面这两条是它落到代码里的样子：
 *
 * # 一、三个写口，一个执行口都没有
 *
 * {@link createImprovementProposal} 登记一条提案；
 * {@link retryImprovementProposal} 把一条停下来的提案**退回流程重走**；
 * {@link disableVerifiedLearningEntry} 把一条经验标成「以后别照这条办」。
 *
 * **判据不是「是不是 POST」，是「它会不会让某件事绕过 Policy 直接发生」**——
 * 重试之后每一步照旧走 Action / Policy / Capability / Audit，
 * 而且红级重试**必须绑一个已经批过的 Action**，这个口自己不签发批准。
 * 关掉一条经验只改那一行的状态，不碰任何生产系统。
 *
 * 没有 accept / execute / apply / deploy / rollback，一个都没有。
 *
 * 这不是「暂时还没做」，是设计。`ai-os.test.ts` 里有一条用例盯着这个文件，
 * 多一个写口它会当场红并报出函数名——与 OS-0 那侧
 * `TestAiOs_没有任何绕过Policy的执行口` 是同一条规矩的前端一半。
 *
 * # 二、状态与人点都是读出来的，不是这一层算出来的
 *
 * `state`、`verification`、`requires_human_click`、`human_clicked_at` 一律照抄响应。
 * 前端一处都不推导——尤其是 `production_verified`：那一档在库里由
 * `module_evaluation_runs_production_verified_needs_pass` 兜着
 * （environment=production + outcome=passed + 已结束），
 * **前端要是自己算一遍，就等于凭空造了一个平台从没验过的结论。**
 */

export type AiModule = Schemas['AiModule']
export type ModuleEvaluationRun = Schemas['ModuleEvaluationRun']
export type ImprovementProposal = Schemas['ImprovementProposal']
export type ImprovementProposalCreate = Schemas['ImprovementProposalCreate']
export type VerifiedLearningEntry = Schemas['VerifiedLearningEntry']

export type AiOsRisk = Schemas['AiOsRisk']
export type AiOsVerification = Schemas['AiOsVerification']
export type AiOsEnvironment = Schemas['AiOsEnvironment']
export type AiOsEvaluationOutcome = Schemas['AiOsEvaluationOutcome']
export type AiOsModuleState = Schemas['AiOsModuleState']
export type AiOsProposalState = Schemas['AiOsProposalState']
export type AiOsLearningState = Schemas['AiOsLearningState']
export type AiOsStopReason = Schemas['AiOsStopReason']
export type AiOsLedgerState = Schemas['AiOsLedgerState']
export type AiOsOperationReceipt = Schemas['AiOsOperationReceipt']
export type AiOsApprovalRef = Schemas['AiOsApprovalRef']
export type ProposalRetryRequest = Schemas['ProposalRetryRequest']
export type LearningEntryDisableRequest = Schemas['LearningEntryDisableRequest']

/** 两个受控写口回的东西：主体本身 + 一份操作回执。 */
export type RetriedProposal = ImprovementProposal & { operation: AiOsOperationReceipt }
export type DisabledLearningEntry = VerifiedLearningEntry & { operation: AiOsOperationReceipt }

/**
 * 一次操作一个键，**调用方在动手那一刻生成，重试沿用同一个**。
 *
 * 与 `static-sites.ts` 里那个是同一件事，理由也一样：在这一层随手生成的话，
 * 每次重试都是一个新键，幂等就成了摆设——而它失效的样子和正常一模一样。
 * 契约那段话说得更直白：一条重复的提案会真的派出去一次开发任务。
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/** 分页参数。契约里 limit 上限 200，这里不替调用方设默认——默认在服务端。 */
export interface AiOsPage {
  cursor?: string
  limit?: number
}

/** 只看某一个模块的。留空是这个项目下全部。 */
export interface AiOsModuleFilter extends AiOsPage {
  moduleId?: string
}

export async function listAiModules(
  projectId: string,
  page: AiOsPage = {},
): Promise<Schemas['AiModuleList']> {
  return call('/projects/{projectId}/ai-os/modules', () =>
    api.GET('/projects/{projectId}/ai-os/modules', {
      params: { path: { projectId }, query: { cursor: page.cursor, limit: page.limit } },
    }),
  )
}

export async function getAiModule(projectId: string, moduleId: string): Promise<AiModule> {
  return call('/projects/{projectId}/ai-os/modules/{moduleId}', () =>
    api.GET('/projects/{projectId}/ai-os/modules/{moduleId}', {
      params: { path: { projectId, moduleId } },
    }),
  )
}

export async function listModuleEvaluationRuns(
  projectId: string,
  filter: AiOsModuleFilter = {},
): Promise<Schemas['ModuleEvaluationRunList']> {
  return call('/projects/{projectId}/ai-os/evaluations', () =>
    api.GET('/projects/{projectId}/ai-os/evaluations', {
      params: {
        path: { projectId },
        query: { moduleId: filter.moduleId, cursor: filter.cursor, limit: filter.limit },
      },
    }),
  )
}

export async function getModuleEvaluationRun(
  projectId: string,
  evaluationId: string,
): Promise<ModuleEvaluationRun> {
  return call('/projects/{projectId}/ai-os/evaluations/{evaluationId}', () =>
    api.GET('/projects/{projectId}/ai-os/evaluations/{evaluationId}', {
      params: { path: { projectId, evaluationId } },
    }),
  )
}

export async function listImprovementProposals(
  projectId: string,
  filter: AiOsModuleFilter = {},
): Promise<Schemas['ImprovementProposalList']> {
  return call('/projects/{projectId}/ai-os/proposals', () =>
    api.GET('/projects/{projectId}/ai-os/proposals', {
      params: {
        path: { projectId },
        query: { moduleId: filter.moduleId, cursor: filter.cursor, limit: filter.limit },
      },
    }),
  )
}

export async function getImprovementProposal(
  projectId: string,
  proposalId: string,
): Promise<ImprovementProposal> {
  return call('/projects/{projectId}/ai-os/proposals/{proposalId}', () =>
    api.GET('/projects/{projectId}/ai-os/proposals/{proposalId}', {
      params: { path: { projectId, proposalId } },
    }),
  )
}

/**
 * 提一条改进提案。**这一组唯一的写口。**
 *
 * 载荷 `additionalProperties: false`，而且**不含** `state` / `development_task_id` /
 * `human_clicked_at` / `audit_event_id` / `version`——多传一个字段是 400，
 * 不是被忽略。生成的 `ImprovementProposalCreate` 类型已经把这件事定死在编译期：
 * 想偷塞一个 `state` 进去，`tsc` 当场报多余属性。
 *
 * 新建的提案一律落在 `proposed`。**这一层不假设、不断言、也不显示那个假设**——
 * 落在哪一档看响应里的 `state`，那是服务端的判断。
 */
export async function createImprovementProposal(
  projectId: string,
  input: ImprovementProposalCreate,
  idempotencyKey: string,
): Promise<ImprovementProposal> {
  return call('/projects/{projectId}/ai-os/proposals', () =>
    api.POST('/projects/{projectId}/ai-os/proposals', {
      params: { path: { projectId }, header: { 'X-Idempotency-Key': idempotencyKey } },
      body: input,
    }),
  )
}

export async function listVerifiedLearningEntries(
  projectId: string,
  filter: AiOsModuleFilter = {},
): Promise<Schemas['VerifiedLearningEntryList']> {
  return call('/projects/{projectId}/ai-os/learning', () =>
    api.GET('/projects/{projectId}/ai-os/learning', {
      params: {
        path: { projectId },
        query: { moduleId: filter.moduleId, cursor: filter.cursor, limit: filter.limit },
      },
    }),
  )
}

export async function getVerifiedLearningEntry(
  projectId: string,
  entryId: string,
): Promise<VerifiedLearningEntry> {
  return call('/projects/{projectId}/ai-os/learning/{entryId}', () =>
    api.GET('/projects/{projectId}/ai-os/learning/{entryId}', {
      params: { path: { projectId, entryId } },
    }),
  )
}


/**
 * 让一条停下来的提案再走一遍。
 *
 * # 幂等键与建提案那个**分开算**
 *
 * 同一条提案会被重试很多次，共用一个键的话第二次会被当成重放静默吞掉。
 * 键的寿命由 `app/ai-os-operation.ts` 管——**那不是随手一件事**：
 * 失败过的键是终态，拿它再打一次只会回当初那份失败，
 * 而界面要是把那当成「又试了一次」，用户会以为自己在重试，其实一次都没发生。
 *
 * # `expected_version` 必填，没有「不填就强写」这一档
 *
 * 那一档存在的话，所有调用方最省事的做法就是不填。
 */
export async function retryImprovementProposal(
  projectId: string,
  proposalId: string,
  input: ProposalRetryRequest,
  idempotencyKey: string,
): Promise<RetriedProposal> {
  return call('/projects/{projectId}/ai-os/proposals/{proposalId}/retry', () =>
    api.POST('/projects/{projectId}/ai-os/proposals/{proposalId}/retry', {
      params: {
        path: { projectId, proposalId },
        header: { 'X-Idempotency-Key': idempotencyKey },
      },
      body: input,
    }),
  )
}

/**
 * 别再用这条经验。
 *
 * **只改这条记录的状态，不碰任何生产系统**——它是「以后别照这条办」，
 * 不是「把照它办过的事撤回来」（那是回滚，另一条路）。
 *
 * `reason` 必填：一条「被关掉但说不出为什么」的经验，
 * 下一个看到它的人只会把它再打开。
 */
export async function disableVerifiedLearningEntry(
  projectId: string,
  entryId: string,
  input: LearningEntryDisableRequest,
  idempotencyKey: string,
): Promise<DisabledLearningEntry> {
  return call('/projects/{projectId}/ai-os/learning/{entryId}/disable', () =>
    api.POST('/projects/{projectId}/ai-os/learning/{entryId}/disable', {
      params: {
        path: { projectId, entryId },
        header: { 'X-Idempotency-Key': idempotencyKey },
      },
      body: input,
    }),
  )
}
